import { Controller } from "@hotwired/stimulus"

// One add-on on the wizard's extra care step. "Add" counts every pet, the
// steppers change the dog and cat counts, and "Remove" (or counting no pets)
// takes it off again. Only the hidden counts are submitted; each change is
// announced so the step can preview the estimate.
export default class extends Controller {
  static targets = ["offer", "chosen", "summary", "quantity", "value", "decrement", "increment"]
  static values = { fees: Object }

  add() {
    this.quantityTargets.forEach((input) => { input.value = this.maximum(input) })
    this.changed()
  }

  remove() {
    this.quantityTargets.forEach((input) => { input.value = 0 })
    this.changed()
  }

  increment({ params: { species } }) {
    this.step(species, 1)
  }

  decrement({ params: { species } }) {
    this.step(species, -1)
  }

  // A count above the pets, kept from before they changed, comes down to the
  // most allowed on the first press.
  step(species, delta) {
    const input = this.quantityFor(species)
    if (!input) return

    input.value = Math.min(this.maximum(input), Math.max(0, this.quantity(input) + delta))
    this.changed()
  }

  changed() {
    const focused = this.element.contains(document.activeElement) ? document.activeElement : null
    this.render()
    // The button pressed may have just been hidden; keep focus in the add-on.
    if (focused?.closest("[hidden]")) {
      const panel = this.offerTarget.hidden ? this.chosenTarget : this.offerTarget
      panel.querySelector("button")?.focus()
    }
    this.dispatch("changed")
  }

  render() {
    const total = this.quantityTargets.reduce((sum, input) => sum + this.quantity(input), 0)
    this.offerTarget.hidden = total > 0
    this.chosenTarget.hidden = total === 0

    this.quantityTargets.forEach((input) => {
      const species = input.dataset.species
      const quantity = this.quantity(input)
      this.targetFor(this.valueTargets, species).textContent = quantity
      this.targetFor(this.decrementTargets, species).disabled = quantity === 0
      this.targetFor(this.incrementTargets, species).disabled = quantity >= this.maximum(input)
    })

    const cents = this.quantityTargets.reduce(
      (sum, input) => sum + this.quantity(input) * (this.feesValue[input.dataset.species] || 0),
      0
    )
    this.summaryTarget.textContent = `${total} ${total === 1 ? "pet" : "pets"} · +${this.price(cents)}`
  }

  quantityFor(species) {
    return this.quantityTargets.find((input) => input.dataset.species === species)
  }

  targetFor(targets, species) {
    return targets.find((target) => target.dataset.species === species)
  }

  quantity(input) {
    return Number.parseInt(input.value, 10) || 0
  }

  maximum(input) {
    return Number.parseInt(input.dataset.max, 10) || 0
  }

  // Add-on prices in whole dollars without cents, as BookingRequestHelper#addon_price
  // formats them.
  price(cents) {
    const digits = cents % 100 === 0 ? 0 : 2
    return (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    })
  }
}
